(() => {
  if (window.MaisonPdpDispatchBootstrapped) return;
  window.MaisonPdpDispatchBootstrapped = true;
  const TEST_ID = 'MC_PDP_DISPATCHCUTOFFANDESTIMATEDARRIVAL_KLEONE';
  const active = new WeakMap();

  function observed(date) {
    const day = date.getUTCDay();
    if (day === 6) return new Date(date.getTime() - 86400000);
    if (day === 0) return new Date(date.getTime() + 86400000);
    return date;
  }

  function nthWeekday(year, month, weekday, ordinal) {
    const first = new Date(Date.UTC(year, month, 1));
    return new Date(Date.UTC(year, month, 1 + (weekday - first.getUTCDay() + 7) % 7 + 7 * (ordinal - 1)));
  }

  function lastWeekday(year, month, weekday) {
    const last = new Date(Date.UTC(year, month + 1, 0));
    return new Date(Date.UTC(year, month, last.getUTCDate() - (last.getUTCDay() - weekday + 7) % 7));
  }

  function usFederalHoliday(date) {
    const key = keyOf(date);
    const year = date.getUTCFullYear();
    for (const candidateYear of [year - 1, year, year + 1]) {
      const dates = [
        observed(new Date(Date.UTC(candidateYear, 0, 1))),
        nthWeekday(candidateYear, 0, 1, 3),
        nthWeekday(candidateYear, 1, 1, 3),
        lastWeekday(candidateYear, 4, 1),
        observed(new Date(Date.UTC(candidateYear, 5, 19))),
        observed(new Date(Date.UTC(candidateYear, 6, 4))),
        nthWeekday(candidateYear, 8, 1, 1),
        nthWeekday(candidateYear, 9, 1, 2),
        observed(new Date(Date.UTC(candidateYear, 10, 11))),
        nthWeekday(candidateYear, 10, 4, 4),
        observed(new Date(Date.UTC(candidateYear, 11, 25))),
      ];
      if (dates.some((holiday) => keyOf(holiday) === key)) return true;
    }
    return false;
  }

  function parseDates(value) {
    if (value.trim().toUpperCase() === 'US_FEDERAL') return usFederalHoliday;
    if (value.trim().toUpperCase() === 'NONE') return new Set();
    if (!value.trim()) return null;
    const dates = value.split(/[\s,]+/).filter(Boolean);
    if (!dates.every((date) => /^\d{4}-\d{2}-\d{2}$/.test(date) && keyOf(utcDate(date)) === date)) return null;
    return new Set(dates);
  }

  function parseWeekdays(value) {
    if (!/^[0-6](\s*,\s*[0-6])*$/.test(value.trim())) return null;
    return new Set(value.split(',').map((day) => Number(day.trim())));
  }

  function parseTransit(value, country) {
    const line = value.split(/\r?\n/).find((entry) => entry.trim().toUpperCase().startsWith(`${country}:`));
    if (!line) return null;
    const match = line.trim().toUpperCase().match(/^([A-Z]{2}):(\d{1,2})-(\d{1,2})$/);
    if (!match || match[1] !== country) return null;
    const min = Number(match[2]);
    const max = Number(match[3]);
    return min >= 1 && max >= min && max <= 30 ? { min, max } : null;
  }

  function localParts(formatter, instant) {
    const values = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value }) => [type, value]));
    return {
      year: Number(values.year), month: Number(values.month), day: Number(values.day),
      hour: Number(values.hour), minute: Number(values.minute),
    };
  }

  function keyOf(date) {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  }

  function utcDate(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }

  function cutoffInstant(date, hour, minute, formatter) {
    const desired = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hour, minute);
    let guess = desired;
    // At the configured cutoff, repeated conversion also handles daylight-saving changes.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const actual = localParts(formatter, new Date(guess));
      guess += desired - Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute);
    }
    return new Date(guess);
  }

  function nextDispatch(now, formatter, hour, minute, weekdays, holidays) {
    const today = localParts(formatter, now);
    const calendarDate = new Date(Date.UTC(today.year, today.month - 1, today.day));
    for (let offset = 0; offset < 60; offset += 1) {
      const candidate = new Date(calendarDate.getTime() + offset * 86400000);
      if (!weekdays.has(candidate.getUTCDay()) || isHoliday(holidays, candidate)) continue;
      const deadline = cutoffInstant(candidate, hour, minute, formatter);
      if (deadline <= now) continue;
      return { date: candidate, deadline, today: offset === 0 };
    }
    return null;
  }

  function addCarrierDays(dispatchDate, days, holidays) {
    const date = new Date(dispatchDate);
    let count = 0;
    let crossedHoliday = false;
    while (count < days) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (isHoliday(holidays, date)) crossedHoliday = true;
      if (date.getUTCDay() === 0 || date.getUTCDay() === 6 || isHoliday(holidays, date)) continue;
      count += 1;
    }
    return { date, crossedHoliday };
  }

  function isHoliday(holidays, date) {
    return typeof holidays === 'function' ? holidays(date) : holidays.has(keyOf(date));
  }

  function init(card) {
    if (active.has(card)) return;
    const arrival = card.closest('form')?.querySelector(`[data-maison-dispatch-arrival="${card.dataset.blockId}"]`);
    if (!arrival) return;

    const cutoffMatch = card.dataset.cutoffTime.trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    const weekdays = parseWeekdays(card.dataset.dispatchWeekdays);
    const dispatchHolidays = parseDates(card.dataset.dispatchHolidays);
    if (!cutoffMatch || !weekdays || !dispatchHolidays || !card.dataset.timeZone.trim() || !card.dataset.timeZoneLabel.trim()) return;

    let formatter;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: card.dataset.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      });
      formatter.format(new Date());
    } catch (_) {
      return;
    }

    const hour = Number(cutoffMatch[1]);
    const minute = Number(cutoffMatch[2]);
    const country = card.dataset.countryCode.toUpperCase();
    if (country !== 'US') return;
    const transit = parseTransit(card.dataset.transitRanges, country);
    const carrierHolidays = parseDates(card.dataset.carrierHolidays);
    const timeLabel = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', hour: 'numeric', minute: minute ? '2-digit' : undefined, hourCycle: 'h12',
    }).format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
    const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const heading = card.querySelector('[data-dispatch-heading]');
    const promise = card.querySelector('[data-dispatch-promise]');
    const detail = card.querySelector('[data-dispatch-detail]');

    function update() {
      const now = new Date();
      const dispatch = nextDispatch(now, formatter, hour, minute, weekdays, dispatchHolidays);
      if (!dispatch) {
        delete card.dataset.maisonDispatchReady;
        delete arrival.dataset.maisonDispatchReady;
        return;
      }

      if (dispatch.today) {
        const remaining = Math.ceil((dispatch.deadline.getTime() - now.getTime()) / 60000);
        const hours = String(Math.floor(remaining / 60)).padStart(2, '0');
        const minutes = String(remaining % 60).padStart(2, '0');
        const countdown = document.createElement('em');
        countdown.textContent = `${hours}h ${minutes}m`;
        heading.textContent = "Get it with today's dispatch";
        promise.replaceChildren(document.createTextNode('Order within '), countdown);
        detail.textContent = `For same-day dispatch · ${timeLabel} ${card.dataset.timeZoneLabel} cutoff`;
      } else {
        const date = dateFormatter.format(dispatch.date);
        heading.textContent = `Next dispatch ${date}`;
        promise.textContent = `Order now for ${date} dispatch`;
        detail.textContent = `Next scheduled dispatch · ${timeLabel} ${card.dataset.timeZoneLabel} cutoff`;
      }
      card.dataset.maisonDispatchReady = 'true';

      if (transit && carrierHolidays) {
        const first = addCarrierDays(dispatch.date, transit.min, carrierHolidays);
        const last = addCarrierDays(dispatch.date, transit.max, carrierHolidays);
        let latest = last.date;
        if (last.crossedHoliday) latest = addCarrierDays(latest, 2, carrierHolidays).date;
        arrival.textContent = `Estimated delivery to ${card.dataset.countryName}: ${dateFormatter.format(first.date)}–${dateFormatter.format(latest)}${last.crossedHoliday ? ' (includes 2 extra business days for holidays)' : ''}`;
        arrival.dataset.maisonDispatchReady = 'true';
      } else {
        arrival.textContent = '';
        delete arrival.dataset.maisonDispatchReady;
      }
    }

    update();
    const timer = window.setInterval(update, 15000);
    active.set(card, timer);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(`[maison-test-id="${TEST_ID}"][data-maison-dispatch-cutoff]`).forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-maison-dispatch-cutoff]').forEach((card) => {
      window.clearInterval(active.get(card));
      active.delete(card);
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
